import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MatchSearchFilters, EMPTY_FILTERS } from './useTurquoiseMatchPreview';

export interface TurquoiseTorihikisaki {
  torihikisaki_cd: number;
  torihikisaki_name: string;
  torihikisaki_kname: string;
  torihikisaki_kbn: string;
  post_no: string;
  address1: string;
  tel_no: string;
  upd_date: string;
}

// GCP中継エンドポイント (VPCコネクタ+固定IP経由でオンプレへ直接アクセス。旧Heteml中継の置き換え)
const BACKEND_API_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/turquoise_torihikisaki';

export const useTurquoiseTorihikisakiTable = () => {
  const [data, setData] = useState<TurquoiseTorihikisaki[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchWord, setSearchWord] = useState('');
  // 詳細検索条件 (旧画面 accounting/tq_partner_list.js の検索パネル相当)。
  // 取込レビューと同じ型を使い、CGI 側の Process=Search へ項目別条件として渡す
  const [filters, setFilters] = useState<MatchSearchFilters>(EMPTY_FILTERS);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        Process: 'Search',
        SearchWord: searchWord,
        LIMIT: pageSize,
        OFFSET: page * pageSize,
        // 項目別条件 (空文字・既定値は CGI 側で無視される)
        CodeStart: filters.code_start,
        CodeEnd: filters.code_end,
        CompanyName: filters.company_name,
        Address: filters.address,
        Tel: filters.tel,
        Fax: filters.fax,
        Mobile: filters.mobile,
        Membership: filters.membership,
        HideDeleted: filters.hide_deleted ? 'true' : '',
        ViewSort: filters.kana_sort ? 'true' : '',
        // 登録日 (MAKE_DATE) の範囲。終了日は当日を含む (CGI 側で翌日 0 時未満と比較)
        MakeDateStart: filters.make_date_start,
        MakeDateEnd: filters.make_date_end,
      };

      const response = await axios.post(BACKEND_API_URL, payload, {
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      });

      let raw = response.data;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        const startIndex = trimmed.search(/[[{]/);
        if (startIndex !== -1) {
          try {
            raw = JSON.parse(trimmed.substring(startIndex));
          } catch (e) {
            console.warn('Turquoise レスポンス JSON パース失敗:', e);
          }
        }
      }

      const td = raw?.turquoisedata;
      if (td && Array.isArray(td.items)) {
        setData(td.items);
        setTotalCount(Number(td.totalCount || 0));
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('ターコイズ取引先データ取得エラー:', err);
      if (err.response) {
        setError(`サーバーエラー (${err.response.status})`);
      } else if (err.request) {
        setError('ネットワークエラーが発生しました');
      } else {
        setError(err.message || 'データ取得に失敗しました');
      }
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchWord, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applySearch = useCallback((word: string) => {
    setSearchWord(word);
    setPage(0);
  }, []);

  // 詳細検索からの実行。word を渡すとフリーワードも同時に更新する
  // (入力欄を空にした後に詳細検索すると、適用済みの旧ワードが AND で残るのを防ぐため)
  const applyFilters = useCallback((next: MatchSearchFilters, word?: string) => {
    setFilters(next);
    if (word !== undefined) setSearchWord(word);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    error,
    page,
    pageSize,
    searchWord,
    filters,
    setPage,
    setPageSize,
    applySearch,
    applyFilters,
    refresh: fetchData,
  };
};
